/**
 * Robot presence: what a robot mark may honestly claim right now (twin
 * slice 1, plan W3 step 7).
 *
 * "Solid means reporting now." Everything the 2D map, the twin, the
 * drawer, the attention list and the locate resolver say about whether
 * a robot is here comes from this one classification, so no two of
 * them can disagree.
 *
 * PRECEDENCE, first match wins:
 *
 *   1. stub or reason  The worker's roster stub (no level) or any
 *                      `unavailable_reason`. SHAPE FIRST: a stub is
 *                      re-stamped every second, so its receipts advance
 *                      like a live robot's and it must never reach the
 *                      age rules. A localisation reason (or status
 *                      `pending-localize`) is `unknown`: the robot is
 *                      there and powered but nobody can say where, so
 *                      no mark at all ("?" on its floor). Every other
 *                      reason, and a stub with none, is `lost`.
 *   2. sentinel        A pose of exactly (0, 0) on a real level is the
 *                      adapter's LOST sentinel: `lost`, never drawn at
 *                      the origin.
 *   3. offline         status `offline`: `lost`.
 *   4. riding          Inside a car (`cabinClearanceM === 0`, NOT the
 *                      0.225 m release clearance, which includes a
 *                      robot boarding on the landing) while the car is
 *                      on another floor or moving -- and ONLY while the
 *                      robot's own receipts keep arriving: its receipt
 *                      age below `LOST_AFTER_MS` (contract §12 A6). The
 *                      robot's own pose still names the floor it
 *                      boarded on, so it is not drawn; the car is. From
 *                      `QUIET_AFTER_MS` it is a QUIET rider: still
 *                      riding, flagged `quiet`. At `LOST_AFTER_MS` --
 *                      or at an age that cannot be trusted (negative,
 *                      NaN) -- it is `lost`, a ghost at its last real
 *                      pose: the car's records keep arriving and keep
 *                      the ride "seen", so without this a robot silent
 *                      for minutes inside a car read as riding, and
 *                      counted as reporting. Seen fewer than two times
 *                      (a REST seed, a replayed row) it is still riding
 *                      below `LOST_AFTER_MS`, flagged `checking`: rule
 *                      6 comes after this one.
 *   5. settling        Within `AFTER_RIDE_MS` of the last ride, under
 *                      the same receipt-age gate: the pose is
 *                      "updating after lift", drawn dimmed; `lost` from
 *                      `LOST_AFTER_MS`; flagged `checking` when seen
 *                      fewer than two times. (A robot frozen inside a
 *                      car that comes back to its floor and stops gets
 *                      here with its receipts already old.)
 *                      With no receipt age at all (nothing is watched
 *                      yet) neither rule applies, and the robot reads
 *                      `checking`, as every robot then does.
 *   6. checking        Fewer than two receipts since the last reset:
 *                      a record seen once could be a replay. Lost if
 *                      even that one is `LOST_AFTER_MS` old.
 *   7. live/quiet/lost By the device-clock receipt age (`liveness`).
 *
 * WHERE A MARK GOES. Live, quiet, checking and settling are drawn at
 * the record's own pose. Lost is drawn as a ghost at the newest REAL
 * pose known -- this page's own (`robot-receipts`) or the recorder's
 * (`GET .../robots/last_poses`), whichever is newer -- or not at all
 * when neither exists. Unknown and riding draw nothing.
 *
 * LAST SEEN IN A CAR. A lost robot whose ghost stands inside a car
 * (`isInsideCar` on the ghost's pose) carries that car as `liftName`:
 * "No signal · last seen in SL1 at 14:02". It is read off the ghost,
 * not off the rule that made the robot lost, because a silent rider
 * goes lost two ways: by this page's receipt age (rule 4), and -- while
 * the adapter's roster still arrives -- by the worker's
 * `state_channel_stale` stub, which replaces its record once
 * `NEXSOSS_ENTITY_STALE_AFTER_MS` (30 s by default) passes with no
 * state (`rmf_bridge._on_robot_roster`) and is decided by shape (rule
 * 1). Either way the car is the one its last real pose was in, and
 * matches where the ghost is drawn. Whether the words may also say "No
 * signal" depends on the cause, not on the car (`robot-lines`): the
 * (0, 0) sentinel names the car without it.
 *
 * STABLE IDENTITY. `PresenceTracker` hands back the SAME object for a
 * robot whose signature has not changed, and the same Map when no robot
 * changed. The signature is built from what a mark shows, never from
 * `updated_at_ms`: a parked robot re-stamped every second must not
 * re-render anything.
 *
 * ONE RIDE MEMORY PER SITE. Settling depends on a ride having been SEEN,
 * so where that memory lives decides who agrees. It is the site's
 * `RideBook`, not any one tracker's: `useLiveData` feeds it on every
 * flush where robots and lifts both flow, whichever screen is open, and
 * every tracker notes what it sees and reads from it. A drawer, a Fleet
 * row or a card mounted after a ride therefore reads the same settling
 * the map does, rather than a solid mark at the pose the ride left
 * untrustworthy.
 */
import type { LiftViewModel } from '../../api/adapters';
import type { RobotStateView } from '../../api/rmf-client';
import type { RealPose } from '../../api/robot-receipts';
import { robotReceiptKey } from '../../api/robot-receipts';
import type { RobotLastPose } from '../../api/twin';
import { AFTER_RIDE_MS, LOST_AFTER_MS, classify, msUntilNextBoundary } from '../../state/liveness';
import { cabinClearanceM } from '../Tasks/task-ownership';

/**
 * The settling words (contract §2, `AFTER_RIDE_MS`), in sentence form.
 * The robot drawer's and Fleet table's location line (`robot-lines`)
 * and the locate resolver's note both say exactly this, so no surface
 * rewords the state another names.
 */
export const SETTLING_TEXT = 'Position updating after lift.';

export type PresenceKind =
  | 'live'
  | 'quiet'
  | 'lost'
  | 'checking'
  | 'settling'
  | 'riding'
  | 'unknown';

/** Which rule decided it. */
export type PresenceCause =
  | 'stub'
  | 'reason'
  | 'sentinel'
  | 'offline'
  | 'riding'
  | 'after-ride'
  | 'first-receipt'
  | 'age';

/**
 * Lost causes where the robot ITSELF is unheard, so "No signal" is true:
 * its own receipts stopped (`age`), the worker stands in for it (a
 * `stub`, or a `reason` -- on a lost robot always an unreachable one:
 * the localisation reasons are `unknown`), or it is `offline`. NOT the
 * (0, 0) `sentinel`: the adapter parks RMF there for every lost reason
 * outside a ride freeze (`RobotCommandHandle._lost_reason`), including a
 * robot that is reporting but has lost its place -- re-localising in the
 * cabin after a ride -- and the record carries no reason to tell which.
 * Allow-listed, so a cause added later claims nothing until it is put here.
 *
 * One set for every surface that words a lost robot: the drawer's and
 * Fleet's location line (`robot-lines`) and the attention list's "Right
 * now" rows (`state/attention/right-now`).
 */
export const UNHEARD_CAUSES: ReadonlySet<PresenceCause> = new Set<PresenceCause>([
  'age',
  'stub',
  'reason',
  'offline',
]);

/**
 * The `unavailable_reason`s that mean "reporting, but not placeable":
 * the worker maps exactly these to `pending-localize`
 * (`rmf_bridge._ROBOT_STATUS_BY_ROSTER_REASON`). Every other reason --
 * `edge_offline`, `not_reporting`, `state_channel_stale`, `unknown`, or
 * one this console has never heard of -- means nobody can reach it.
 */
export const LOCALISATION_REASONS: ReadonlySet<string> = new Set([
  'not_localized',
  'location_status_absent',
  'pose_not_on_graph',
]);

export interface PresencePose {
  readonly map: string;
  readonly x: number;
  readonly y: number;
  readonly yaw: number | null;
  /** `live`: the record's own pose. `session`/`recorded`: a ghost, from
   *  this page's memory or the pose recorder. */
  readonly source: 'live' | 'session' | 'recorded';
  /** A ghost's worker stamp ("last recorded at HH:MM"); null for live. */
  readonly atMs: number | null;
}

export interface RobotPresence {
  /** `<fleet>/<robot>`. */
  readonly key: string;
  readonly kind: PresenceKind;
  readonly cause: PresenceCause;
  /** `unavailable_reason`, verbatim; null when none. */
  readonly reason: string | null;
  /** Where to draw, or null for nothing. */
  readonly pose: PresencePose | null;
  /** The floor a mark-less robot belongs to (`unknown`'s "?" row): its
   *  newest real pose's level. Null when drawn, or when none is known. */
  readonly level: string | null;
  /** The car: while riding and settling, and -- for a lost robot whose
   *  ghost stands inside one -- the car it was last seen in. */
  readonly liftName: string | null;
  /**
   * The robot's own receipts are `QUIET_AFTER_MS` to `LOST_AFTER_MS` old
   * where the kind does not already say so: always true for `quiet`,
   * and true for a QUIET RIDER (`riding`, still in the car). False for
   * every other kind: checking and settling are dimmed whatever their
   * age, so the band changes nothing they show, and lost and unknown
   * are not judged by it.
   *
   * A CONTRACT FOR CONSUMERS, not a rendering: a surface that shows a
   * rider dims it while `quiet` and never shows it as reporting now. In
   * slice 1 stage 2 no surface shows a rider by presence alone -- the
   * 2D draws none (the car is drawn), and the drawer's and Fleet's
   * lines read the same for a quiet rider as a live one except "heard
   * N s ago" (battery is "—" for every rider) -- so nothing reads it
   * yet. Its readers to be: the twin's rider marks (stages 3-4) and
   * right-now (`state/attention`), which today works the band out from
   * the receipt age itself.
   */
  readonly quiet: boolean;
  /**
   * Seen fewer than two times since the last reset (rule 6's two-receipt
   * rule) where the kind does not already say so: always true for
   * `checking`, and true for a rider or a settling robot seen once.
   * Riding and settling come BEFORE rule 6, so a REST seed or a replayed
   * row of a robot inside a car -- possibly silent for minutes -- reads
   * riding until its age reaches `LOST_AFTER_MS`. It is not vouched for:
   * nothing may count it as reporting (`robotsOnline` reads "?") or say
   * it was just heard ("heard" reads "checking…"). False for every other
   * kind: live and quiet have two receipts by rule 7, and lost and
   * unknown are not judged by it.
   */
  readonly checking: boolean;
  /** What a mark shows, as one string. Never includes `updated_at_ms`. */
  readonly signature: string;
}

/**
 * Is the robot's centre inside this car's rectangle? Clearance 0, not
 * within the 0.225 m release margin: a robot waiting on the landing
 * with its nose over the sill is within the margin and is NOT in the
 * car. W4's locate resolver uses this same predicate (parity, X33).
 */
export function isInsideCar(
  lift: Pick<LiftViewModel, 'levels' | 'ref' | 'refYaw' | 'width' | 'depth'>,
  location: { map: string; x: number; y: number },
): boolean {
  return cabinClearanceM(lift, location) === 0;
}

/** The car whose cabin `pose` stands in (`isInsideCar`), or undefined. */
export function carContaining(
  pose: { map: string; x: number; y: number },
  lifts: readonly LiftViewModel[] | undefined,
): LiftViewModel | undefined {
  return lifts?.find((lift) => isInsideCar(lift, pose));
}

/**
 * The car this robot is riding, or undefined: inside it, and the car is
 * on another floor than the robot's own pose says, or the ride is
 * moving (the session's `moving` phase, or the car's own motion). A
 * robot inside a car standing at its own floor -- PL3's same-floor
 * crossing, a boarding, an exit -- is not riding.
 */
export function ridingLift(
  robot: Pick<RobotStateView, 'location'>,
  lifts: readonly LiftViewModel[] | undefined,
): LiftViewModel | undefined {
  const loc = robot.location;
  if (!loc || !loc.map || !lifts?.length) return undefined;
  for (const lift of lifts) {
    if (!isInsideCar(lift, loc)) continue;
    const otherFloor = Boolean(lift.currentLevel) && lift.currentLevel !== loc.map;
    const moving = lift.session?.phase === 'moving' || lift.motionState === 'moving';
    if (otherFloor || moving) return lift;
  }
  return undefined;
}

/**
 * When each robot of one site was last seen riding, and in which car:
 * the settling window's memory, shared by every surface (see the header).
 * Times are the receipts' monotonic clock (`RobotReceipts.now()`).
 */
export class RideBook {
  private readonly _rides = new Map<string, { at: number; liftName: string }>();

  /** `key` was seen riding `liftName` at `now`. A ride is remembered
   *  from the LAST moment it was seen, not from when it began. */
  note(key: string, liftName: string, now: number): void {
    const prev = this._rides.get(key);
    if (prev && prev.at > now) return;
    this._rides.set(key, { at: now, liftName });
  }

  /** Notes every robot that is riding now (`ridingLift`). */
  observe(
    robots: readonly Pick<RobotStateView, 'name' | 'fleet' | 'location'>[] | undefined,
    lifts: readonly LiftViewModel[] | undefined,
    now: number,
  ): void {
    if (!robots?.length || !lifts?.length) return;
    for (const robot of robots) {
      const lift = ridingLift(robot, lifts);
      if (lift) this.note(robotReceiptKey(robot.fleet, robot.name), lift.name, now);
    }
  }

  /** Ms since `key` was last seen riding, and the car; null when it has
   *  not been, or not within `AFTER_RIDE_MS` (which it then forgets). */
  sinceRide(key: string, now: number): { ms: number; liftName: string } | null {
    const ride = this._rides.get(key);
    if (!ride) return null;
    const ms = now - ride.at;
    if (ms >= AFTER_RIDE_MS) {
      this._rides.delete(key);
      return null;
    }
    return { ms, liftName: ride.liftName };
  }
}

const _rideBooks = new Map<string, RideBook>();

/** The page's ride book for `site`, created on first use. */
export function rideBookFor(site: string): RideBook {
  let book = _rideBooks.get(site);
  if (!book) {
    book = new RideBook();
    _rideBooks.set(site, book);
  }
  return book;
}

/** Tests only: forget every site's rides. */
export function _resetRideBooksForTests(): void {
  _rideBooks.clear();
}

/** The newer of two real poses (worker stamps, the same clock); this
 *  page's own on a tie. */
export function pickGhost(
  session: RealPose | undefined,
  recorded: Pick<RobotLastPose, 'level' | 'x' | 'y' | 'yaw' | 't'> | undefined,
): PresencePose | null {
  const fromSession: PresencePose | null = session
    ? { map: session.map, x: session.x, y: session.y, yaw: session.yaw, source: 'session', atMs: session.atMs }
    : null;
  const usable =
    recorded && recorded.level !== '' && !(recorded.x === 0 && recorded.y === 0) ? recorded : undefined;
  const fromRecorder: PresencePose | null = usable
    ? { map: usable.level, x: usable.x, y: usable.y, yaw: usable.yaw ?? null, source: 'recorded', atMs: usable.t }
    : null;
  if (!fromSession) return fromRecorder;
  if (!fromRecorder) return fromSession;
  return (fromRecorder.atMs ?? 0) > (fromSession.atMs ?? 0) ? fromRecorder : fromSession;
}

export interface PresenceFacts {
  readonly robot: Pick<RobotStateView, 'name' | 'fleet' | 'status' | 'location' | 'unavailableReason'>;
  /** Strictly newer records received since the last reset. */
  readonly receiptCount: number;
  /** Monotonic ms since the last receipt, or since the watch began when
   *  there has been none; null when nothing is being watched. */
  readonly receiptAgeMs: number | null;
  /** The car being ridden now (`ridingLift`). */
  readonly riding: Pick<LiftViewModel, 'name'> | undefined;
  /** Monotonic ms since this robot was last seen riding, and the car. */
  readonly sinceRide: { readonly ms: number; readonly liftName: string } | null;
  readonly ghost: PresencePose | null;
  /** The car the ghost stands in (`carContaining` on its pose); absent
   *  when there is no ghost or it is in no car. */
  readonly ghostCar?: Pick<LiftViewModel, 'name'>;
}

function signatureOf(p: Omit<RobotPresence, 'signature'>): string {
  const pose = p.pose
    ? [p.pose.source, p.pose.map, p.pose.x, p.pose.y, p.pose.yaw ?? '', p.pose.source === 'live' ? '' : p.pose.atMs]
    : [];
  const quiet = p.quiet ? 'quiet' : '';
  const checking = p.checking ? 'checking' : '';
  const head = [p.kind, p.cause, p.reason ?? '', p.liftName ?? '', p.level ?? '', quiet, checking];
  return [...head, ...pose].join('|');
}

function finish(p: Omit<RobotPresence, 'signature'>): RobotPresence {
  return { ...p, signature: signatureOf(p) };
}

/** Pure: one robot's presence from the facts about it. */
export function classifyPresence(f: PresenceFacts): RobotPresence {
  const { robot } = f;
  const key = robotReceiptKey(robot.fleet, robot.name);
  const reason = robot.unavailableReason ? robot.unavailableReason : null;
  const loc = robot.location;
  const base = {
    key,
    reason,
    liftName: null as string | null,
    level: null as string | null,
    quiet: false,
    checking: false,
  };
  const lost = (cause: PresenceCause): RobotPresence =>
    finish({
      ...base,
      kind: 'lost',
      cause,
      pose: f.ghost,
      level: null,
      liftName: f.ghost ? (f.ghostCar?.name ?? null) : null,
    });

  // 1. Stub or reason -- by SHAPE first, before anything about age.
  const stub = !loc || typeof loc.map !== 'string' || loc.map === '';
  if (stub || reason !== null || robot.status === 'pending-localize') {
    // 'stub' only when the shape alone decided it; a status or reason
    // on a real-shaped record is the record saying so itself.
    const cause: PresenceCause = stub && reason === null ? 'stub' : 'reason';
    const notPlaceable =
      robot.status === 'pending-localize' || (reason !== null && LOCALISATION_REASONS.has(reason));
    if (notPlaceable) {
      return finish({ ...base, kind: 'unknown', cause, pose: null, level: f.ghost?.map ?? null });
    }
    return lost(cause);
  }
  // From here the record has a level.
  const here = loc!;
  // 2. The LOST sentinel.
  if (here.x === 0 && here.y === 0) return lost('sentinel');
  // 3. Offline.
  if (robot.status === 'offline') return lost('offline');
  // The robot's OWN receipt age, which riding and settling are held to:
  // the car's records keep a ride "seen" whether or not the robot talks.
  const own = f.receiptAgeMs === null ? null : classify(f.receiptAgeMs);
  // Riding and settling come before rule 6: one seen once keeps its
  // reading, unvouched.
  const seenOnce = f.receiptCount < 2;
  // 4. Riding, while its own receipts keep arriving.
  if (f.riding && own !== null) {
    if (own === 'lost') return lost('age');
    return finish({
      ...base,
      kind: 'riding',
      cause: 'riding',
      pose: null,
      liftName: f.riding.name,
      quiet: own === 'quiet',
      checking: seenOnce,
    });
  }
  const livePose: PresencePose = {
    map: here.map,
    x: here.x,
    y: here.y,
    yaw: typeof here.yaw === 'number' && Number.isFinite(here.yaw) ? here.yaw : null,
    source: 'live',
    atMs: null,
  };
  // 5. Settling after a ride, under the same gate.
  if (f.sinceRide && f.sinceRide.ms >= 0 && f.sinceRide.ms < AFTER_RIDE_MS && own !== null) {
    if (own === 'lost') return lost('age');
    return finish({
      ...base,
      kind: 'settling',
      cause: 'after-ride',
      pose: livePose,
      liftName: f.sinceRide.liftName,
      checking: seenOnce,
    });
  }
  // 6. Checking: not yet seen to advance twice.
  if (seenOnce) {
    if (f.receiptAgeMs !== null && !(f.receiptAgeMs < LOST_AFTER_MS)) return lost('age');
    return finish({ ...base, kind: 'checking', cause: 'first-receipt', pose: livePose, checking: true });
  }
  // 7. By age.
  const kind = own ?? 'lost';
  if (kind === 'lost') return lost('age');
  return finish({ ...base, kind, cause: 'age', pose: livePose, quiet: kind === 'quiet' });
}

/** Ms until this robot's reading would change with no new data; null
 *  when only new data can change it. */
export function presenceBoundaryMs(p: RobotPresence, f: PresenceFacts): number | null {
  // A rider turns quiet at 10 s and lost at 30 s, as any record does.
  if (p.kind === 'riding') {
    return f.receiptAgeMs === null ? null : msUntilNextBoundary(f.receiptAgeMs);
  }
  // Settling ends when the window does, or when its receipts reach
  // LOST_AFTER_MS first.
  if (p.kind === 'settling' && f.sinceRide) {
    const settled = AFTER_RIDE_MS - f.sinceRide.ms;
    return f.receiptAgeMs === null ? settled : Math.min(settled, LOST_AFTER_MS - f.receiptAgeMs);
  }
  if (p.kind === 'checking') {
    return f.receiptAgeMs === null ? null : LOST_AFTER_MS - f.receiptAgeMs;
  }
  if ((p.kind === 'live' || p.kind === 'quiet') && f.receiptAgeMs !== null) {
    return msUntilNextBoundary(f.receiptAgeMs);
  }
  return null;
}

/** What the tracker reads receipts through (a `RobotReceipts` fits). */
export interface ReceiptSource {
  get(key: string): { count: number };
  ageMs(key: string, now: number): number | null;
  lastRealPose(key: string): RealPose | undefined;
}

export interface PresenceInputs {
  readonly robots: readonly RobotStateView[] | undefined;
  readonly lifts: readonly LiftViewModel[] | undefined;
  readonly receipts: ReceiptSource;
  /** `last_poses` keyed by bare robot name; undefined when unsupported
   *  or not loaded (session-only ghosts). */
  readonly recorded: Readonly<Record<string, RobotLastPose>> | undefined;
  /** The site's ride memory (`rideBookFor`): noted into and read from,
   *  so every surface's settling window is the same one. */
  readonly rides: RideBook;
  /** Monotonic now, the receipts' clock. */
  readonly now: number;
}

/**
 * Classifies every robot and keeps identities stable across calls. The
 * ride memory it needs is the site's shared `RideBook`, never its own.
 */
export class PresenceTracker {
  private _byKey = new Map<string, RobotPresence>();
  private _out: ReadonlyMap<string, RobotPresence> = new Map();
  private _next: number | null = null;

  update(inputs: PresenceInputs): ReadonlyMap<string, RobotPresence> {
    const next = new Map<string, RobotPresence>();
    let changed = false;
    let boundary: number | null = null;
    for (const robot of inputs.robots ?? []) {
      const key = robotReceiptKey(robot.fleet, robot.name);
      const rec = inputs.recorded?.[robot.name];
      const recorded = rec && (!rec.fleet || rec.fleet === robot.fleet) ? rec : undefined;
      const riding = ridingLift(robot, inputs.lifts);
      // What this surface sees goes into the shared memory too (the
      // same predicate `RideBook.observe` applies on the flush).
      if (riding) inputs.rides.note(key, riding.name, inputs.now);
      const ghost = pickGhost(inputs.receipts.lastRealPose(key), recorded);
      const facts: PresenceFacts = {
        robot,
        receiptCount: inputs.receipts.get(key).count,
        receiptAgeMs: inputs.receipts.ageMs(key, inputs.now),
        riding,
        sinceRide: inputs.rides.sinceRide(key, inputs.now),
        ghost,
        ghostCar: ghost ? carContaining(ghost, inputs.lifts) : undefined,
      };
      const fresh = classifyPresence(facts);
      const prev = this._byKey.get(key);
      const kept = prev && prev.signature === fresh.signature ? prev : fresh;
      if (kept !== prev) changed = true;
      next.set(key, kept);
      const b = presenceBoundaryMs(fresh, facts);
      if (b !== null && (boundary === null || b < boundary)) boundary = Math.max(0, b);
    }
    if (next.size !== this._byKey.size) changed = true;
    this._byKey = next;
    this._next = boundary;
    if (changed) this._out = next;
    return this._out;
  }

  /** Ms until the soonest reading changes with no new data, from the
   *  last `update`; null when none will. Feed it to `useBoundaryTick`. */
  nextBoundaryMs(): number | null {
    return this._next;
  }
}
